import { useSchedulingQuery as useQuery } from "../../../../hooks/ReactQueryHooks/useSchedulingQuery";
import { schedulingGet } from "../../../api/Scheduling/client";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Tab, Tabs, Chip } from "@mui/material";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import "./DashboardAppointments.css";
import moment from "moment-timezone";
import { useSchedulingTimeZone } from "../../../../hooks/useSchedulingTimeZone";
import { useSchedulingOrg } from "../../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { useAppointmentsQuery } from "../../../../hooks/ReactQueryHooks/useAppointmentsQuery";
import { useServiceCategoriesQuery } from "../../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { buildCalendarEvents } from "../calendar/eventModel";
import SchedulingCalendar from "../calendar/SchedulingCalendar";
import TimeZoneSelect from "../TimeZoneSelect";
import AppointmentsList from "./AppointmentsList";
import AppointmentDetailModal from "./AppointmentDetailModal";

const DashboardAppointmentsCard = ({ organizationRef: orgOverride, practitionerRef: practitionerOverride, listOnly = false }) => {
  const { organizationRef: sessionOrg, lockedOrgId, practitionerRef: selfRef, user, organizationTimeZone, excludedOrganizationRefs } = useSchedulingOrg();
  const organizationRef = orgOverride || lockedOrgId || sessionOrg;
  const practitionerRef = practitionerOverride || selfRef;
  const scope = orgOverride ? "own" : "descendants";
  const displayZone = useSchedulingTimeZone(user);
  const { timeZone } = displayZone;
  const [tab, setTab] = useState("today");
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  useEffect(() => { setSelected(null); }, [organizationRef, practitionerRef, excludedOrganizationRefs]);
  const [calendarRange, setCalendarRange] = useState({});
  const { data = [], isLoading, isError, refetch } = useAppointmentsQuery({
    organizationRef,
    scope, excludedOrganizationRefs,
    practitionerRef,
    ...calendarRange,
    enabled: !!practitionerRef && view === "calendar" && !!calendarRange.startsAt,
  });
  const { data: categories = [] } = useServiceCategoriesQuery({ scope, excludedOrganizationRefs, organizationRef, active: undefined });
  const events = useMemo(
    () => buildCalendarEvents({ appointments: data, categories, showBookings: true }),
    [data, categories]
  );

  const dayStart = moment.tz(timeZone).startOf("day");
  const countsQuery = useQuery(["scheduling-appointment-counts", excludedOrganizationRefs, organizationRef, practitionerRef, scope, dayStart.toISOString(), timeZone], async ({ signal, consistency }) => {
    const result = await schedulingGet("appointment", { organizationRef, scope, excludedOrganizationRefs, practitionerRef, summary: "counts", startsAt: dayStart.toISOString(), endsAt: dayStart.clone().add(1,"day").toISOString() }, { signal, consistency });
    if (!result?.counts || !["today","upcoming","requested","rejected","completed"].every(key => Number.isInteger(result.counts[key]) && result.counts[key] >= 0)) throw new Error("Could not load appointment counts.");
    return result.counts;
  }, { enabled: !!organizationRef && !!practitionerRef && view === "list", staleTime: 30000, refetchOnWindowFocus: false, retry: false });
  const counts = countsQuery.data;

  const status =
    tab === "requested"
      ? "proposed"
      : tab === "rejected"
        ? "cancelled"
        : tab === "completed"
          ? "fulfilled"
          : "booked";
  const range =
    tab === "today"
      ? { startsAt: moment.tz(timeZone).startOf("day").toISOString(), endsAt: moment.tz(timeZone).startOf("day").add(1, "day").toISOString() }
      : tab === "upcoming"
        ? { startsAt: moment.tz(timeZone).add(1, "day").startOf("day").toISOString() }
        : {};

  if (!practitionerRef) return <Alert severity="info">Your practitioner record is unavailable.</Alert>;

  return (
    <section className="appointment-dashboard" aria-labelledby={listOnly ? undefined : "dashboard-appointments-title"} aria-label={listOnly ? "Appointments" : undefined}>
      {!listOnly && <div className="appointment-dashboard-heading">
        <div className="appointment-dashboard-title"><span className="appointment-dashboard-icon"><CalendarTodayOutlined /></span>
          <div><h1 id="dashboard-appointments-title">Appointments</h1><p>Manage your patient appointments</p></div>
        </div>
        <div className="appointment-dashboard-tools">
          <span className="appointment-dashboard-date">{moment.tz(timeZone).format("dddd, MMMM D")}</span>
          <div className="appointment-view-switch" role="group" aria-label="Appointment view">
            <button type="button" aria-pressed={view === "list"} onClick={() => setView("list")}>List</button>
            <button type="button" aria-pressed={view === "calendar"} onClick={() => { setSelected(null); setView("calendar"); }}>Calendar</button>
          </div>
        </div>
      </div>}
      {view === "list" ? <>
      {countsQuery.isError && <Alert severity="error" action={<Button onClick={() => countsQuery.refetch()}>Retry counts</Button>}>Could not load appointment counts.</Alert>}
      <Tabs className="appointment-dashboard-tabs" value={tab} onChange={(_event, value) => setTab(value)}
        variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Appointment views">
        {[["today", "Today"], ["upcoming", "Upcoming"], ["requested", "Requested"], ["rejected", "Rejected"], ["completed", "Completed"]].map(([key, label]) =>
          <Tab key={key} value={key} id={`appointment-tab-${key}`} aria-controls="appointment-dashboard-panel"
            label={<span className="appointment-tab-label">{label}<Chip label={countsQuery.isError || !counts ? "—" : counts[key]} size="small" /></span>} />)}
      </Tabs>
      <div id="appointment-dashboard-panel" role="tabpanel" aria-labelledby={`appointment-tab-${tab}`}>
      <AppointmentsList
        scope={scope}
        organizationRef={organizationRef}
        practitionerRef={practitionerRef}
        status={status}
        startsAt={range.startsAt}
        endsAt={range.endsAt}
        dashboard
        dashboardTab={tab}
      />
      </div>
      </> : <div className="appointment-dashboard-calendar">
        {isLoading && <p role="status">Loading appointments…</p>}
        {isError ? <div className="appointment-list-error"><Alert severity="error"
          action={<Button color="inherit" size="small" onClick={refetch}>Retry</Button>}>Unable to load appointments.</Alert></div> :
          <SchedulingCalendar timeZone={timeZone} events={isLoading ? [] : events} onDatesSet={({ start, end }) => setCalendarRange({ startsAt: start.toISOString(), endsAt: end.toISOString() })} extraViews="timeGridWeek,timeGridDay,listWeek"
            timeZoneControl={<TimeZoneSelect value={displayZone.preference} onChange={displayZone.setPreference}
              localZone={displayZone.deviceTimeZone} organizationZone={organizationTimeZone} />}
            onEventClick={(_event, props) => { if (props.kind === "appointment") setSelected(props.source); }} />}
      </div>}
      {selected ? <AppointmentDetailModal timezoneName={timeZone} appointment={selected} organizationRef={organizationRef}
        onHide={() => setSelected(null)} onChanged={() => setSelected(null)} /> : null}
    </section>
  );
};

export default DashboardAppointmentsCard;
